import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { REQUESTS_FORM_ID } from 'Enum/RequestsFormId';
import { useDictionary, useGeneralDictionary, useIsDesktop } from 'Hooks';
import { Vi, Exit } from 'Images';
import { useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
interface ISuccessDialog {
  open: boolean;
  handleClose: () => void;
  title: string;
  reqNum: number;
  reqDateTime: string;
}

export const SuccessDialog = ({
  open,
  handleClose,
  title,
  reqNum,
  reqDateTime,
}: ISuccessDialog) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.Requests);
  const generalDictionary = useGeneralDictionary();
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  const formName = useRef(useParams().formName);

  const getLinkInfo = () => {
    switch (formName.current) {
      case 'update-personal-details':
        return dictionary.successDialog.linkInfo.updatePersonalDetails;

      case 'expense-claim':
      case 'update-bank-details':
        return '';

      default:
        return dictionary.successDialog.linkInfo.general;
    }
  };

  const getLinkText = () => {
    switch (formName.current) {
      case 'update-personal-details':
        return dictionary.successDialog.linkText.updatePersonalDetails;

      case 'update-bank-details':
        return '';

      case 'expense-claim':
        return dictionary.successDialog.linkText.expenseClaim;

      default:
        return dictionary.successDialog.linkText.general;
    }
  };

  const getLinkUrl = () => {
    switch (formName.current) {
      case 'update-personal-details':
        return dictionary.successDialog.personalAreaUrl;

      case 'update-bank-details':
        return '';

      case 'expense-claim':
        return dictionary.successDialog.expenseClaimUrl;

      default:
        return dictionary.successDialog.requestsLobbyUrl;
    }
  };

  const getSubtitle = () => {
    switch (formName.current) {
      case 'expense-claim':
        return dictionary.successDialog.expenseClaimInfo;

      default:
        return dictionary.successDialog.info;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            overflowY: 'visible',
          },
        }}
        maxWidth='lg'
      >
        <Avatar
          src={Vi}
          alt=''
          sx={{
            width: isDesktop ? '137px' : '100px',
            height: isDesktop ? '137px' : '100px',
            position: 'absolute',
            top: isDesktop ? '-50px' : '-40px',
            left: isDesktop ? 'calc(50% - 68.5px)' : 'calc(50% - 50px)',
            alignItems: 'center',
          }}
        />
        <Stack
          alignItems='center'
          textAlign='center'
          sx={{
            width: isDesktop ? '50vw' : undefined,
            pb: isDesktop ? '80px' : '40px',
          }}
        >
          <DialogTitle
            sx={{
              p: isDesktop ? '123px 0 16px' : '90px 6px 16px',
              fontSize: isDesktop ? '28px' : '24px',
              fontWeight: '700',
            }}
          >
            <IconButton
              aria-label='close'
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 38,
                top: 38,
              }}
            >
              <Avatar
                src={Exit}
                alt={generalDictionary.closeWindow}
                sx={{ width: '18px', height: '18px' }}
              />
            </IconButton>
            {title}
          </DialogTitle>
          <Typography
            p={isDesktop ? undefined : '0 20px'}
            fontSize={'1rem'}
            fontWeight={'400'}
          >
            {getSubtitle()}
          </Typography>
          <DialogContent sx={{ p: '24px 0 50px' }}>
            <DialogContentText color='inherit' component='div'>
              <Stack flexDirection='row' justifyContent='center' gap='5px'>
                <Typography
                  fontSize={isDesktop ? '24px' : '16px'}
                  lineHeight='32px'
                  fontWeight='300'
                >
                  {dictionary.successDialog.requestId}
                </Typography>
                <Typography
                  fontSize={isDesktop ? '24px' : '16px'}
                  lineHeight='32px'
                  fontWeight={isDesktop ? undefined : '700'}
                >
                  {reqNum}
                </Typography>
              </Stack>
              <Stack flexDirection='row' justifyContent='center' gap='5px'>
                <Typography
                  mb='24px'
                  fontSize={isDesktop ? '24px' : '16px'}
                  lineHeight='32px'
                  fontWeight='300'
                >
                  {dictionary.successDialog.date}
                </Typography>
                <Typography
                  mb='24px'
                  fontSize={isDesktop ? '24px' : '16px'}
                  lineHeight='32px'
                  fontWeight={isDesktop ? undefined : '700'}
                >
                  {reqDateTime}
                </Typography>
              </Stack>
            </DialogContentText>
            <Typography mr='6px' display='inline-block'>
              {getLinkInfo()}
            </Typography>
            <Link
              sx={{ textDecorationLine: 'underline', display: 'inline-block' }}
              href={getLinkUrl()}
            >
              {getLinkText()}
            </Link>
          </DialogContent>
          <DialogActions
            sx={
              isDesktop
                ? undefined
                : { width: '100%', p: '0 20px', justifyContent: 'center' }
            }
          >
            {formName.current === REQUESTS_FORM_ID.General ||
              (formName.current === REQUESTS_FORM_ID.UpdatePersonalDetails && (
                <Button
                  variant='outlined'
                  sx={{
                    p: isDesktop ? '5px 26px' : '5px 15px',
                    width: isDesktop ? undefined : '70%',
                    mr: isDesktop ? '24px' : '0',
                    fontSize: '1rem',
                    fontWeight: '700',
                    border: '1px solid #1752AF',
                  }}
                  onClick={() => navigate('/universalRequest')}
                >
                  {dictionary.successDialog.newRequest}
                </Button>
              ))}
            <Button
              variant='contained'
              sx={{
                width: isDesktop ? undefined : '30%',
                p: isDesktop ? '5px 74px' : '5px 15px',
                fontWeight: '400',
                fontSize: '1rem',
              }}
              onClick={() => {
                if (window.location.pathname.includes('update-bank-details'))
                  navigate('/universalRequest');
                else {
                  window.location.assign(
                    window.location.pathname.includes('update-personal-details')
                      ? dictionary.successDialog.personalAreaUrl
                      : dictionary.successDialog.digitalAppliesUrl
                  );
                }
              }}
              autoFocus
            >
              {dictionary.successDialog.finish}
            </Button>
          </DialogActions>
        </Stack>
      </Dialog>
    </>
  );
};
